/**
 * General Settings Component - Admin Dashboard
 *
 * Home for admin-tunable runtime settings (BMF-117): the abandoned-payment
 * auto-cancel wait and the UPI QR delivery agents show at the door. Further
 * global knobs belong here rather than scattered across the feature screens.
 *
 * The Delivery Fee GST picker that used to live here was removed by BMF-155:
 * the delivery fee no longer carries a rate of its own, it inherits the GST
 * rate of the products it is apportioned across, so there was nothing left for
 * an admin to choose. See OrderTaxService.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Settings as SettingsIcon,
  Timer,
  QrCode,
  Upload,
  Edit2,
  Save,
  X,
  Loader2,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import settingsService, {
  PAYMENT_QR_ACCEPT,
  PAYMENT_QR_MAX_BYTES,
  PAYMENT_QR_ALLOWED_LABEL,
} from '../../services/settingsService';

const GeneralSettings = () => {
  const [settings, setSettings] = useState({
    autoCancelMinutes: 10,
    autoCancelMinutesMin: 2,
    autoCancelMinutesMax: 120,
    paymentQrImageUrl: null,
  });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState('10');
  const [saving, setSaving] = useState(false);
  const [uploadingQr, setUploadingQr] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const qrInputRef = useRef(null);

  const showSuccess = useCallback((msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  }, []);

  useEffect(() => {
    let cancelled = false;
    settingsService.getGeneralSettings()
      .then((result) => {
        if (cancelled) return;
        // On failure the service still returns usable defaults, so render them
        // and surface the reason rather than leaving the card empty.
        setSettings(result.data);
        setForm(String(result.data.autoCancelMinutes));
        if (!result.success) setError(result.error);
      })
      .catch(() => {
        // getGeneralSettings resolves even on API failure, so this only fires on
        // an unexpected error — surface it and keep the default values in state.
        if (!cancelled) setError('Failed to load settings');
      })
      .finally(() => {
        // Always clear loading, even on rejection, so the card can never get
        // stuck on the spinner.
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    setError('');
    const minutes = Number(form);
    const { autoCancelMinutesMin: min, autoCancelMinutesMax: max } = settings;

    // Client-side pre-check purely for a faster message; the server re-validates
    // against the same bounds it handed us in getGeneralSettings.
    if (!Number.isInteger(minutes)) {
      setError('Please enter a whole number of minutes');
      return;
    }
    if (minutes < min || minutes > max) {
      setError(`Please enter a value between ${min} and ${max} minutes`);
      return;
    }

    setSaving(true);
    const result = await settingsService.updateAutoCancelMinutes(minutes);
    setSaving(false);

    if (result.success) {
      setSettings((prev) => ({ ...prev, autoCancelMinutes: result.data.minutes }));
      setEditing(false);
      showSuccess('Auto-cancel wait updated');
    } else {
      setError(result.error);
    }
  };

  const handleCancel = () => {
    setForm(String(settings.autoCancelMinutes));
    setError('');
    setEditing(false);
  };

  const handleQrSelected = async (event) => {
    const file = event.target.files?.[0];
    // Clear the input straight away so picking the same file again still fires
    // onChange — otherwise a failed upload can't be retried without switching files.
    event.target.value = '';
    if (!file) return;

    setError('');

    // Pre-checks purely for a faster message; the server enforces both again.
    if (!PAYMENT_QR_ACCEPT.split(',').includes(file.type)) {
      setError(`Please choose a ${PAYMENT_QR_ALLOWED_LABEL} image`);
      return;
    }
    if (file.size > PAYMENT_QR_MAX_BYTES) {
      setError(`The QR image must be under ${Math.round(PAYMENT_QR_MAX_BYTES / (1024 * 1024))} MB`);
      return;
    }

    setUploadingQr(true);
    const result = await settingsService.uploadPaymentQr(file);
    setUploadingQr(false);

    if (result.success) {
      setSettings((prev) => ({ ...prev, paymentQrImageUrl: result.data.paymentQrImageUrl }));
      showSuccess('Payment QR updated — agents will see it on their next collection');
    } else {
      setError(result.error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading settings...
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <SettingsIcon className="w-6 h-6 text-green-600" />
        <h1 className="text-xl font-semibold text-gray-800">General Settings</h1>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          {success}
        </div>
      )}

      {/* Abandoned payment auto-cancel */}
      <div className="bg-white rounded-xl p-5 shadow-sm mb-6 max-w-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Timer className="w-5 h-5 text-green-600" />
            <h2 className="font-semibold text-gray-800">Payment Confirmation Wait</h2>
          </div>
          {!editing ? (
            <button
              onClick={() => { setForm(String(settings.autoCancelMinutes)); setEditing(true); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            >
              <Edit2 className="w-3.5 h-3.5" /> Edit
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-60"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save
              </button>
              <button
                onClick={handleCancel}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                <X className="w-3.5 h-3.5" /> Cancel
              </button>
            </div>
          )}
        </div>

        <p className="text-sm text-gray-500 mb-4">
          When a customer opens the online payment page but never completes the payment,
          the order is held while we wait for the payment to confirm. If it still has not
          been paid after this long, the order is cancelled automatically and its stock is
          returned. Payments that did go through are never cancelled &mdash; every order is
          re-checked against the payment gateway first.
        </p>

        {editing ? (
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Cancel unpaid orders after
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={settings.autoCancelMinutesMin}
                max={settings.autoCancelMinutesMax}
                step={1}
                value={form}
                onChange={(e) => setForm(e.target.value)}
                className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <span className="text-sm text-gray-600">minutes</span>
            </div>
            <p className="text-xs text-gray-400 mt-1.5">
              Allowed range: {settings.autoCancelMinutesMin}&ndash;{settings.autoCancelMinutesMax} minutes.
              Takes effect within a minute, and applies to orders that are already waiting.
            </p>
          </div>
        ) : (
          <div>
            <span className="text-xs font-medium text-gray-500">Cancel unpaid orders after</span>
            <p className="text-2xl font-semibold text-gray-800 mt-0.5">
              {settings.autoCancelMinutes}
              <span className="text-base font-normal text-gray-500 ml-1.5">minutes</span>
            </p>
          </div>
        )}
      </div>

      {/* Payment QR shown by delivery agents */}
      <div className="bg-white rounded-xl p-5 shadow-sm mb-6 max-w-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-green-600" />
            <h2 className="font-semibold text-gray-800">Payment QR Code</h2>
          </div>
          <button
            onClick={() => qrInputRef.current?.click()}
            disabled={uploadingQr}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-60"
          >
            {uploadingQr ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {settings.paymentQrImageUrl ? 'Replace QR' : 'Upload QR'}
          </button>
          <input
            ref={qrInputRef}
            type="file"
            accept={PAYMENT_QR_ACCEPT}
            onChange={handleQrSelected}
            className="hidden"
          />
        </div>

        <p className="text-sm text-gray-500 mb-4">
          The UPI QR code delivery agents show customers to collect payment for a Cash on
          Delivery order at the door. One code is used for every agent and every order, so
          replacing it here changes it everywhere &mdash; agents see the new code the next
          time they open a collection. Accepts {PAYMENT_QR_ALLOWED_LABEL} images up to{' '}
          {Math.round(PAYMENT_QR_MAX_BYTES / (1024 * 1024))} MB.
        </p>

        {settings.paymentQrImageUrl ? (
          <div>
            <span className="text-xs font-medium text-gray-500">Current QR</span>
            <div className="mt-1.5 inline-block p-3 bg-white border border-gray-200 rounded-lg">
              <img
                src={settings.paymentQrImageUrl}
                alt="Payment QR code"
                className="w-44 h-44 object-contain"
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 border-2 border-dashed border-gray-200 rounded-lg text-gray-400">
            <QrCode className="w-8 h-8 mb-2" />
            <p className="text-sm">No payment QR uploaded yet</p>
            <p className="text-xs mt-0.5">Agents cannot collect by UPI until one is added</p>
          </div>
        )}
      </div>

    </div>
  );
};

export default GeneralSettings;
